<script setup>
import SteamGameView from "@/views/SteamGameView.vue";
import { useEventsStore } from "@/stores/events.js";
import { useUserDataStore } from "@/stores/userData";
import { useOwnedGamesStore } from "@/stores/ownedGamesStore.js";
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useBreakpoints, breakpointsTailwind } from "@vueuse/core";
import Footer from "@/components/Footer.vue";
import HeaderView from "@/views/HeaderView.vue";
import Navigation from "@/components/Navigation.vue";
import Card from "@/components/ui/card/Card.vue";
import CardContent from "@/components/ui/card/CardContent.vue";
import AddSteamGamePresenter from "@/presenters/AddSteamGamePresenter.vue";
import Dialog from "@/components/ui/dialog/Dialog.vue";
import DialogContent from "@/components/ui/dialog/DialogContent.vue";
import DialogTitle from "@/components/ui/dialog/DialogTitle.vue";
import DialogDescription from "@/components/ui/dialog/DialogDescription.vue";
import { isDarkMode } from "@/darkmode.js";

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller("lg");

// Need to pass the interests and handle the filtering

const eventsStore = useEventsStore();
const userDataStore = useUserDataStore();
const ownedGamesStore = useOwnedGamesStore();
const route = useRoute();
const router = useRouter();

const currentEventId = route.params.id;
const userId = computed(() => userDataStore.steamId);
const ownedGames = computed(() => ownedGamesStore.getOwnedGames());
const eventProfiles = computed(() => eventsStore.profiles);

const event = computed(() =>
  eventsStore.events.find((e) => e.id === currentEventId)
);

const interests = computed(() =>
  event.value ? eventsStore.getInterests(event.value) : []
);

const searchQuery = ref("");

function setSearch(value) {
  searchQuery.value = value;
}

const filteredInterests = computed(() => {
  let list = interests.value;
  if (searchQuery.value && searchQuery.value.trim() !== "") {
    const query = searchQuery.value.toLowerCase();
    list = list.filter((game) => game.name.toLowerCase().includes(query));
  }
  return list;
});

function addUserToInterest(interest) {
  if (!event.value) return;
  eventsStore.addUserToInterest(event?.value.id, interest, userId.value);
}

function removeUserFromInterest(interest) {
  if (!event.value) return;
  eventsStore.removeUserFromInterest(
    event?.value.id,
    interest.id,
    userId.value
  );
}

function toggleInterest(interest) {
  if (interest.users?.includes(userId.value)) {
    removeUserFromInterest(interest);
  } else {
    addUserToInterest(interest);
  }
}

function toAddGame() {
  router.push({ name: "AddSteamGame", params: { id: currentEventId } });
}

const isDialogOpen = ref(false);
function openDialog() {
  isDialogOpen.value = true;
}
</script>

<template>
  <div class="z-100 w-full min-h-full dark:bg-background">
    <div v-if="!isMobile" class="w-full h-20">
      <Navigation
        :username="userDataStore.userProfile?.nickname"
        :avatarUrl="userDataStore.userProfile.avatarUrl"
      ></Navigation>
    </div>
    <Card
      v-if="!isMobile"
      class="w-full h-full rounded-0 border-0 shadow-none bg-background lg:pt-0 lg:gap-0 px-4 lg:px-22"
    >
      <div
        class="inset-0 z-10 w-full h-50 mb-4"
        style="
          background-image: url('https://images.unsplash.com/photo-1604846887565-640d2f52d564?q=80&w=1331&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D');
          background-size: cover;
          background-position: center;
        "
      >
        <div
          class="flex items-center justify-center h-full text-3xl font-semibold bg-black/50 text-white"
        >
          Find games to play with others
        </div>
      </div>
    </Card>
    <Card
      class="border-0 rounded-0 shadow-none p-4 w-full h-full min-h-svh bg-background pb-50 items-center lg:pt-0 lg:px-20"
    >
      <HeaderView v-if="isMobile" name="Shared Games"></HeaderView>

      <CardContent>
        <SteamGameView
          :interests="filteredInterests"
          :uid="userId"
          :ownedGames="ownedGames"
          :search="searchQuery"
          @changeSearch="setSearch"
          @toggleInterest="toggleInterest"
          @onRouteClick="toAddGame"
          @dialogOpen="openDialog"
          :profiles="eventProfiles"
        ></SteamGameView>
      </CardContent>
    </Card>
    <Dialog v-if="!isMobile" v-model:open="isDialogOpen">
      <DialogContent
        :class="{ dark: isDarkMode }"
        class="lg:max-w-screen-5xl overflow-y-scroll max-h-screen mt-10 shadow-none"
      >
        <DialogTitle class="hidden"></DialogTitle>
        <DialogDescription class="hidden"></DialogDescription>
        <AddSteamGamePresenter :fromInterests="true"></AddSteamGamePresenter>
      </DialogContent>
    </Dialog>
    <Footer v-if="isMobile"></Footer>
  </div>
</template>

<style>
.dark {
  color: #fafafa;
}
</style>
